/**
 * Bedridden Patients Groups API Service
 *
 * Combines data from multiple sources for 3 patient groups:
 * 1. ติดบ้าน/ติดเตียง (Bedridden) - from bedridden_patients table
 * 2. ผู้สูงอายุ (Elderly) - from vulnerable_populations where group_id = 5
 * 3. กลุ่มเสี่ยงสูง (High Risk) - from vulnerable_populations with high risk criteria
 *
 * 💡 Uses mock data when API returns empty
 */

import useSWR from 'swr'
import { apiClient } from '../../shared/api/client'

/**
 * Combined Groups Response Type
 */
export interface BedriddenGroupsResponse {
  bedridden: {
    total: number
    by_type: { home: number; bed: number }
    by_severity: { high: number; medium: number; low: number }
  }
  elderly: {
    total: number
    by_district?: Array<{ district_id: number; count: number }>
  }
  highRisk: {
    total: number
    by_district?: Array<{ district_id: number; count: number }>
  }
}

/**
 * Vulnerable Population Response (from existing API)
 * Actual response structure from VulnerablePopulationController
 */
interface VulnerablePopulationResponse {
  year: number
  totals: {
    grand_total: number
    total_immediate_care: number
    total_receiving_support: number
  }
  by_group?: Array<{
    id: number // This is the group_id
    name_th: string
    name_en: string
    description: string
    icon_name: string
    color_code: string
    display_order: number
    total_population: number
    total_immediate_care: number
    total_receiving_support: number
    avg_percentage: number
  }>
}

/**
 * Mock Data (ใช้เมื่อ API ยังไม่มีข้อมูล)
 */
const MOCK_DATA: BedriddenGroupsResponse = {
  bedridden: {
    total: 810,
    by_type: {
      home: 486, // 60% ติดบ้าน
      bed: 324,  // 40% ติดเตียง
    },
    by_severity: {
      high: 243,   // 30% เสี่ยงสูง
      medium: 405, // 50% เสี่ยงปานกลาง
      low: 162,    // 20% เสี่ยงต่ำ
    },
  },
  elderly: {
    total: 57830, // ผู้สูงอายุทั้งหมดในจังหวัด
  },
  highRisk: {
    total: 8675, // ~15% ของผู้สูงอายุ
  },
}

/**
 * Fetch all 3 groups data
 *
 * Combines:
 * - Bedridden patients (new bedridden_patients table)
 * - Elderly (vulnerable_populations where group_id = 5)
 * - High risk (vulnerable_populations with immediate care)
 */
export function useBedriddenGroups(params?: { district_id?: number; year?: number }) {
  const queryParams = new URLSearchParams()
  if (params?.district_id) {
    queryParams.append('district_id', params.district_id.toString())
  }
  if (params?.year) {
    queryParams.append('year', params.year.toString())
  }

  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''

  // Fetch bedridden stats
  const bedriddenUrl = `/patients/bedridden/stats${queryString}`
  const { data: bedriddenData, error: bedriddenError } = useSWR(
    bedriddenUrl,
    apiClient.fetcher,
    { revalidateOnFocus: false, dedupingInterval: 10000 }
  )

  // Fetch vulnerable populations (for elderly group_id = 5)
  const vulnerableUrl = `/vulnerable-populations${queryString}`
  const { data: vulnerableData, error: vulnerableError } = useSWR<VulnerablePopulationResponse>(
    vulnerableUrl,
    apiClient.fetcher,
    { revalidateOnFocus: false, dedupingInterval: 10000 }
  )

  // Calculate totals from vulnerable data (FIXED: match actual API response)
  const elderlyGroup = vulnerableData?.by_group?.find((g) => g.id === 5) // group_id = 5 for bedridden elderly
  const elderlyTotal = elderlyGroup?.total_population || 0

  // High risk = sum of immediate care from all groups
  const highRiskTotal = elderlyGroup?.total_immediate_care || Math.floor(elderlyTotal * 0.15)

  // Check for authentication errors (401)
  const hasAuthError =
    (bedriddenError && bedriddenError.message?.includes('401')) ||
    (vulnerableError && vulnerableError.message?.includes('401'))

  // Use real data if available, otherwise fallback to mock
  const hasRealData = (bedriddenData?.total && bedriddenData.total > 0) || elderlyTotal > 0

  const combined: BedriddenGroupsResponse = hasRealData
    ? {
        bedridden: {
          total: bedriddenData?.total || 0,
          by_type: bedriddenData?.by_type || { home: 0, bed: 0 },
          by_severity: bedriddenData?.by_severity || { high: 0, medium: 0, low: 0 },
        },
        elderly: {
          total: elderlyTotal,
          by_district: vulnerableData?.by_district?.map((d) => ({
            district_id: d.district_id,
            count: d.total_population,
          })),
        },
        highRisk: {
          total: highRiskTotal,
          by_district: vulnerableData?.by_district?.map((d) => ({
            district_id: d.district_id,
            count: Math.floor(d.total_population * 0.15),
          })),
        },
      }
    : MOCK_DATA

  return {
    data: combined,
    isLoading: false, // Always show data immediately (mock or real)
    error: hasAuthError ? null : (bedriddenError || vulnerableError), // Suppress 401 errors
  }
}
